import React, { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
} from 'reactstrap';
import { UserPermissions } from '../../../constants/Enums';
import { getProfiles } from '../../../redux/actions/profile';
import {
  addMember,
  deleteMember,
  getProjects,
  updateMember,
  updateProject,
} from '../../../redux/actions/projects';
import { SET_PROJECT } from '../../../redux/actions/types';
import store from '../../../redux/store';
import DraggableModal from '../../entities/EntityModals/DraggableModal';
import { hasPermission } from '../../helpers/genericHelper';
import CustomDataTableStyles from '../../layout/DataTableStyles';
import AutoCompleteInput from '../project-components/AutoCompleteInput';
import DeleteModal from '../project-components/DeleteModal';
import ModalError from '../project-components/ModalError';
import RoleSelect from '../project-components/RoleSelect';
import AddMember from '../project-forms/AddMember';

/**
 * Project Members Panel
 *
 * Panel for displaying, adding, editing and deleting project members
 * TODO: To fetch users from the backend and display user names instead of Id
 * TODO: update edit feature when the inputs from the backend is finalized
 *
 * @param toggle: triggers/toggles the modal to close/open
 * @param openPanel: checks if panel is open or closed to change caret
 * @param data: parameter for the specific project in the project details page
 * @param project: place holder for the project state during editing
 * @param deleteMember: redux action for deleting a  member
 * @param updateMember: redux action for updating a  member
 * @param profile: fetches profile of the current user in the system
 */

const MembersPanel = ({
  toggle,
  openPanel,
  data,
  project,
  profile,
  updateMember,
  projectRole,
}) => {
  const [editIds, setEditIds] = useState([]);
  const triggerEdit = (editMember) => {
    setEditIds([...editIds, editMember.user_id]);
  };

  const history = useHistory();

  const removeEditId = (member) => {
    const newIds = Object.assign([], editIds);
    if (newIds.includes(member.user_id)) {
      setEditIds(newIds.filter((id) => id !== member.user_id));
    }
  };
  const [member, setMember] = useState({
    user_id: '',
    role: '',
    user_name: '',
  });
  const { dispatch } = store;
  useEffect(() => {
    setMember({
      ...member,
      user_id: editIds.user_id,
      role: editIds.role,
      user_name: editIds.user_name,
    });
  }, [project]);

  //Event handling for inputs
  const onChange = (event, id) => {
    setMember({
      ...member,
      [event.target.name]: event.target.value,
      user_id: id,
      user_name: id.name,
    });
  };

  //TODO: Method for submission of edited part in the project members
  const onSubmit = async () => {
    updateMember(project?._id, member, project);
    removeEditId(member);
  };

  const selection = (row) => {
    setMember({
      ...member,
      user_id: parseInt(row?.user_id),
    });
  };

  const removeEdit = (row) =>
    removeEditId({
      user_id: row.user_id,
    });

  //Columns for project members table
  const columns = useMemo(() => [
    {
      name: 'ISAT User Name',
      selector: 'user_name',
      sortable: true,
      cell: (row) => <>{row.user_name}</>,
    },
    {
      name: 'Role',
      selector: 'role',
      sortable: true,
      cell: (row) => {
        if (editIds.includes(row.user_id)) {
          if (
            row.role === 'owner' &&
            project.participants.filter((member) => member.role === 'owner')
              .length === 1
          ) {
            return <Fragment>{row.role}</Fragment>;
          } else {
            return (
              <Fragment>
                <RoleSelect
                  onChange={(event) => onChange(event, row.user_id)}
                  data={member.role}
                  defaultValue={row.role}
                  isCreator={hasPermission(
                    profile?.permissions,
                    UserPermissions.Creator
                  )}
                />
              </Fragment>
            );
          }
        } else {
          return <Fragment>{row.role}</Fragment>;
        }
      },
    },
    {
      cell: (row) =>
        data?.baseline === undefined &&
        data?.participants
          ?.find((member) => parseInt(member.user_id) === parseInt(profile?.id))
          ?.role.includes('owner') && (
          <ButtonGroup>
            {editIds.includes(row.user_id) ? (
              <Fragment>
                {row.role === 'owner' &&
                project.participants.filter((member) => member.role === 'owner')
                  .length === 1 ? (
                  <div>
                    <DraggableModal
                      isOpen={editIds.includes(row.user_id)}
                      toggle={() => removeEdit(row)}
                    >
                      <ModalHeader>
                        <div className='modal-header'>Edit Project Member</div>
                      </ModalHeader>
                      <div className='modal-line' />
                      <ModalBody className='modal-body'>
                        There has to be at least one team member with the role
                        owner at all times!
                      </ModalBody>
                      <ModalFooter>
                        <Button onClick={() => removeEdit(row)}>Ok</Button>
                      </ModalFooter>
                    </DraggableModal>
                  </div>
                ) : (
                  <>
                    <Button
                      id={`saveButton${row.user_id}`}
                      className='btn-add'
                      onClick={() => {
                        selection(row.user_id);
                        onSubmit();
                      }}
                    >
                      <i className='fa fa-save btn-icon' />
                    </Button>
                    <UncontrolledTooltip target={`saveButton${row.user_id}`}>
                      Save
                    </UncontrolledTooltip>
                    <Button
                      id={`cancelButton${row.user_id}`}
                      className='btn-danger'
                      onClick={() => {
                        removeEditId(row);
                      }}
                    >
                      <i className='fa fa-times btn-icon' />
                    </Button>
                    <UncontrolledTooltip target={`cancelButton${row.user_id}`}>
                      Cancel
                    </UncontrolledTooltip>
                  </>
                )}
              </Fragment>
            ) : (
              <Fragment>
                {projectRole === 'owner' && (
                  <>
                    {/* Edit Button */}
                    <Button
                      id={`editButton${row?.user_id}`}
                      onClick={() => triggerEdit(row)}
                    >
                      <i className='fa fa-edit' />
                    </Button>
                    <UncontrolledTooltip target={`editButton${row?.user_id}`}>
                      Edit
                    </UncontrolledTooltip>
                    <div style={{ width: 10 }} />
                    {/* Delete Button */}
                    <Button
                      id={`deleteButton${row?.user_id}`}
                      className='btn-danger'
                      onClick={() => deleteToggle(row)}
                    >
                      <i className='fa fa-trash' />
                    </Button>
                    <UncontrolledTooltip target={`deleteButton${row?.user_id}`}>
                      Delete
                    </UncontrolledTooltip>
                    {/* Warning if the selected user is an owner 
                      and there is only one owner in the project members */}
                    {row.role === 'owner' &&
                    project.participants.filter(
                      (member) => member.role === 'owner'
                    ).length === 1 ? (
                      <div>
                        <ModalError
                          isOpen={deleteModal && deleteId === row.user_id}
                          toggle={deleteToggle}
                          message={
                            'There has to be at least one team member with the role owner at all times!'
                          }
                          header={'Ownership Check'}
                          onClick={deleteToggle}
                        />
                      </div>
                    ) : (
                      <>
                        {/* Confirmation Modal if the user wants to remove himself from the group */}
                        {parseInt(profile?.id) === row?.user_id ? (
                          <div>
                            <DeleteModal
                              isOpen={deleteModal && deleteId === row.user_id}
                              toggle={deleteToggle}
                              onClick={async () => {
                                deleteToggle();
                                await deleteTeamMember(row.user_id);
                                await dispatch(getProjects());
                                history.push('/');
                                await dispatch({
                                  type: SET_PROJECT,
                                  payload: undefined,
                                });
                              }}
                              header='Delete Project Member'
                              message='You are about to remove yourself from the team. 
                                You wont have access to this project anymore and will be redirected to the projects page. 
                                Are you sure you want to proceed?'
                            />
                          </div>
                        ) : (
                          <div>
                            {/* Warning if the selected user is an owner 
                              and there is only one owner in the projec members */}
                            <DeleteModal
                              isOpen={deleteModal && deleteId === row.user_id}
                              toggle={deleteToggle}
                              onClick={() => {
                                deleteToggle();
                                deleteTeamMember(row.user_id);
                              }}
                              header='Delete Project Member'
                              message='Are you sure you want to remove the user from the team?'
                            />
                          </div>
                        )}{' '}
                      </>
                    )}
                  </>
                )}
              </Fragment>
            )}
          </ButtonGroup>
        ),
    },
  ]);

  //Sets Modal for deleting project members
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const deleteToggle = (row) => {
    if (!row) {
      setDeleteId(-1);
    } else {
      setDeleteId(row.user_id);
    }
    setDeleteModal(!deleteModal);
  };

  const deleteTeamMember = async (row) => {
    await dispatch(deleteMember(project?._id, row));
  };

  //Method for search input
  const [searchText, setSearchText] = React.useState('');
  const [resetPaginationToggle] = React.useState(false);
  var options = data.participants.map((member) => member.user_name);

  const searchItems =
    data?.participants &&
    data?.participants.filter(
      (member) =>
        member.user_name &&
        member.user_name.toLowerCase().includes(searchText.toLowerCase())
    );

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <AutoCompleteInput
        options={options}
        inputValue={searchText}
        onInputChange={(e, newValue) => {
          setSearchText(newValue);
        }}
        label='Search Project Member'
      />
    );
  }, [searchText, resetPaginationToggle]);

  //Sets Modal for adding new members
  const [addModal, setAddModal] = useState(false);
  const addToggle = () => setAddModal(!addModal);

  //TODO: to add role check on displaying columns
  return (
    <div>
      <CardHeader
        style={{ cursor: 'pointer' }}
        onClick={toggle}
        className='bold'
      >
        {' '}
        {openPanel ? (
          <i className='fa fa-caret-down' />
        ) : (
          <i className='fa fa-caret-right' />
        )}{' '}
        {'  '}Team
      </CardHeader>
      <Collapse isOpen={openPanel}>
        <Card>
          <CardBody>
            <div>
              {((data?.baseline === undefined &&
                data?.participants
                  ?.find(
                    (member) =>
                      parseInt(member.user_id) === parseInt(profile?.id)
                  )
                  ?.role.includes('owner')) ||
                hasPermission(profile?.permissions, UserPermissions.Admin)) && (
                <>
                  {' '}
                  <Button
                    className='btn-add  float-right'
                    onClick={() => addToggle()}
                  >
                    Add New Member
                  </Button>
                  <AddMember
                    modal={addModal}
                    toggle={addToggle}
                    currentMembers={data.participants}
                  />{' '}
                </>
              )}
            </div>
            <DataTable
              defaultSortAsc='id'
              data={searchItems}
              columns={columns}
              pagination
              paginationResetDefaultPage={resetPaginationToggle}
              subHeader
              subHeaderComponent={subHeaderComponentMemo}
              persistTableHead
              customStyles={CustomDataTableStyles}
            />
          </CardBody>
        </Card>
      </Collapse>
    </div>
  );
};

const mapStateToProps = (state) => ({
  profiles: state.profile.profiles,
  profile: state.profile.profile,
  project: state.project.project,
  projectRole: state.profile.projectRole,
});

export default connect(mapStateToProps, {
  updateProject,
  addMember,
  updateMember,
  getProfiles,
})(MembersPanel);
